import React, { memo, forwardRef, useCallback } from 'react';
import { useDatePickerState } from '@react-stately/datepicker';
import { useDatePicker } from '@react-aria/datepicker';
import { mergeProps } from '@react-aria/utils';
import { FocusScope } from '@react-aria/focus';
import { useDateFormatter } from '@react-aria/i18n';
import { AlertIcon } from '@gwc/icons';
import { Calendar } from '../calendar';
import { Popper } from '../popper';
import { Paper } from '../paper';
import { Text, Trails } from '../slots';
import { Typography } from '../typography';
import { TextField } from '../text-field';
import { ClearButton } from '../clear-button';
import { Box } from '../box';
import { date } from '../../utils';
import { CalendarButton } from './CalendarButton';
import { DateInput } from './DateInput';
import { useDatePickerBase } from './useDatePickerBase';
import { DEFAULT_MIN_DATE } from '../calendar/consts';
import styles from './styles.module.css';
const DatePickerBase = memo(forwardRef(function DatePicker(props, ref) {
    const { value, onChange, error, defaultValue, minValue: initialMinValue, maxValue, withTime, children, disabled, readOnly, size, portal, calendarSize, minValueReason, maxValueReason, reasonPlacement, ...rest } = props;
    const dateFormatter = useDateFormatter({});
    // сейчас всегда используется текущая таймзона,
    // если потребуется выбор, то надо дорабатывать stringToDate
    const { timeZone } = dateFormatter.resolvedOptions();
    const minValue = initialMinValue || date.dateToString(DEFAULT_MIN_DATE, timeZone);
    const { inputRef, groupRef, hiddenInputProps, state } = useDatePickerBase({ ...props, minValue, timeZone }, useDatePickerState);
    const { groupProps, labelProps: dateLabelProps, fieldProps, buttonProps, dialogProps, calendarProps, } = useDatePicker({ 'aria-label': props['aria-label'], granularity: state.granularity, isDisabled: disabled, isReadOnly: readOnly }, state, groupRef);
    const onClose = useCallback(() => {
        state.setOpen(false);
    }, [state.setOpen]);
    const onClear = useCallback(() => {
        var _a;
        onChange === null || onChange === void 0 ? void 0 : onChange(null);
        // фокусим первый сегмент
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [onChange]);
    return (React.createElement(Box, { flexDirection: "column", flex: 1, ref: ref },
        React.createElement(TextField, { ...mergeProps(rest, groupProps), ref: groupRef, inputProps: { ref: inputRef }, animated: false, error: error || state.validationState === 'invalid', disabled: disabled, readOnly: readOnly, size: size }, ({ labelProps, inputProps: { value: _, onChange: __, tabIndex, ...inputRestProps }, error, }) => children({
            buttonProps,
            labelProps: mergeProps(dateLabelProps, labelProps),
            inputProps: mergeProps(fieldProps, inputRestProps),
            value,
            onClear,
            error,
            isOpen: state.isOpen,
        })),
        React.createElement("input", { ...hiddenInputProps, className: styles.hiddenInput }),
        React.createElement(Popper, { ...dialogProps, portal: portal, target: groupRef, opened: state.isOpen, onClose: onClose, Component: Paper, p: "m" },
            React.createElement(FocusScope, { contain: true, restoreFocus: true },
                React.createElement(Calendar, { ...calendarProps, minValueReason: minValueReason, maxValueReason: maxValueReason, reasonPlacement: reasonPlacement, size: calendarSize !== null && calendarSize !== void 0 ? calendarSize : size, value: value, defaultValue: defaultValue, minValue: minValue, maxValue: maxValue, onChange: value => {
                        var _a;
                        (_a = calendarProps.onChange) === null || _a === void 0 ? void 0 : _a.call(calendarProps, date.stringToDate(value));
                    } })))));
}));
const DatePickerSimple = memo(forwardRef(function DatePicker(props, ref) {
    const { label, ...rest } = props;
    return (React.createElement(DatePickerBase, { "aria-label": typeof label === 'string' ? label : undefined, ...rest, ref: ref }, ({ labelProps, inputProps, buttonProps, error, value, onClear }) => (React.createElement(React.Fragment, null,
        React.createElement(CalendarButton, { ...buttonProps }),
        React.createElement(Text, null,
            !!label && React.createElement(Typography, { ...labelProps }, label),
            React.createElement(DateInput, { ...inputProps })),
        React.createElement(ClearButton, { hidden: !value, onPress: onClear }),
        error && (React.createElement(Trails.Icon, { color: "error" },
            React.createElement(AlertIcon, null)))))));
}));
export const DatePicker = Object.assign(DatePickerBase, {
    Simple: DatePickerSimple,
});
